import { useEffect, useMemo, useRef, useState } from 'react'
import { alsEingabe, beschriftung, feldname, speicherform, t, zahlenwort, anzahlwort } from './sprache'
import type { Sprachschluessel } from './sprache'
import { stillgelegtAus } from './Stilllegung'
import { TAFELSPALTEN, liesFeldzahl, periodenwort, wertwort } from './Stammdaten'
import type { Feldart, Listeneintrag, Objektartbeschreibung, Pflegeauftrag, Zahlendarstellung } from './typen'
import { Periodenfeld } from './Periodenfeld'
import { Zahlenfeld } from './Zahlenfeld'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { ausPeriodenfeld } from './Zeitraum'
import { meldeErfolg } from './Erfolgsstelle'

/**
 * Der ganze Bereich als **eine geschlossene Tabelle** (7.4 Abschnitt 20).
 *
 * > **Der Auftrag vom 2026-09-19:** *„Stelle den gesamten Bereich zudem als
 * > geschlossene Tabelle dar, die der Anwender schnell copy&pasten kann. Wenn
 * > ich nur 1 Mutter habe ist es anwenderfreundlicher, wenn ich den Eintrag
 * > sofort für alle pasten kann, statt jede Firma separat anzuklicken und es
 * > auszuwählen."*
 *
 * **Das Blatt bleibt, es ist nur nicht mehr der einzige Weg.** Ein
 * Eigenschaftsblatt beantwortet die Frage *„was gilt für diese eine
 * Gesellschaft, und woher kommt es"* — mit Herkunft, Vorschlag und
 * Fassungsreihe. Es beantwortet nicht die Frage *„sieht das überall gleich
 * aus"*, und für achtzehn Beteiligungen mit derselben Mutter ist das die
 * einzige Frage, die zählt.
 *
 * **Drei Eigenschaften, und alle drei folgen aus „geschlossen":**
 *
 * 1. **Die Zellen sind Text, keine Bedienelemente.** Eine Tabelle voller
 *    Klapplisten lässt sich nicht nach Excel kopieren — man bekommt leere
 *    Zellen oder den ganzen Vorrat. Gearbeitet wird auf einer **Auswahl**, wie
 *    in jeder Tabellenkalkulation; das Bedienelement erscheint erst in der
 *    Zelle, in der gerade geschrieben wird.
 * 2. **Kopiert und eingefügt wird über die Zwischenablage des Systems**, nicht
 *    über einen eigenen Speicher. Strg+C und Strg+V lösen `copy` und `paste`
 *    aus; die Tabelle hängt sich dort ein. Damit geht der Weg nach Excel und
 *    zurück, ohne dass jemand eine Datei anfassen muss.
 * 3. **Ein Wert auf viele Zeilen ist der Regelfall, nicht der Sonderfall.**
 *    Enthält die Zwischenablage genau einen Wert und die Auswahl viele Zellen,
 *    bekommt jede denselben — genau der Fall aus dem Auftrag.
 *
 * **Geschrieben wird je Zeile ein Auftrag** (R241: jede Änderung ist eine neue
 * Fassung). Achtzehn Zeilen sind achtzehn Fassungen und achtzehn Rufe; das ist
 * ehrlich und bei vier Gesellschaften unauffällig. Bei vierhundert ist es zu
 * langsam — der Sammelauftrag steht deshalb als offener Punkt in 7.4
 * Abschnitt 20, und die Tafel zeigt währenddessen, wie weit sie ist.
 */

/** Eine Zelle als Koordinate. */
type Zelle = { zeile: number; spalte: number }

/** Eine Spalte der Tafel — Werksfeld, Hausfeld oder der Code. */
type Spalte = {
  feld: string
  beschriftung: string
  art: string
  vorrat: string[]
  verweisart: string | null
  /**
   * Was eine Kommazahl ist — Anteil, Betrag oder Kurs (R840). Sie kommt aus der
   * Feldbeschreibung und nicht aus dem Feldnamen; `null` bei jeder anderen
   * Spalte und bei der Zahl ohne Darstellung.
   */
  darstellung: Zahlendarstellung | null
  /** Der Code steht links und wird hier nicht geändert: Er ist die Kennung der Zeile. */
  nurLesen: boolean
}

/**
 * Ob die Spalte eine Periode trägt (R839 — Eine Periode liest sich als Monat
 * und Jahr).
 *
 * **Eine Stelle für vier Fragen:** was in der Zelle steht, was in die
 * Zwischenablage geht, wie eingefügt wird und welches Feld beim Schreiben
 * erscheint. Vier einzelne Abfragen wären vier Gelegenheiten, eine zu
 * vergessen.
 */
function istPeriode(spalte: Spalte): boolean {
  return spalte.art === 'periode' && spalte.vorrat.length === 0 && !spalte.verweisart
}

/** Eine gespeicherte Periode als Zahl — `null`, wo keine steht. */
function alsPeriode(wert: string): number | null {
  const zahl = Number(wert)
  return wert && Number.isInteger(zahl) ? zahl : null
}

/**
 * Ob die Spalte eine Zahl trägt (R840 — Eine Zahl in den Stammdaten steht wie
 * im Bericht).
 *
 * **Wieder eine Stelle für alle Fragen**, wie bei der Periode darüber: was in
 * der Zelle steht, dass sie rechts steht (R763 — Eine Zahl steht rechts), was
 * in die Zwischenablage geht, wie eingefügt wird und welches Feld beim
 * Schreiben erscheint.
 */
function istZahl(spalte: Spalte): boolean {
  return (spalte.art === 'dezimal' || spalte.art === 'zahl')
    && spalte.vorrat.length === 0
    && !spalte.verweisart
}

/**
 * Eine getippte oder eingefügte Zahl in der Speicherform dieser Spalte —
 * `null`, wo sie sich nicht lesen lässt (R840).
 *
 * **Die Feldart `zahl` ist in der Tafel immer die ganze Zahl des Werks**
 * (Sortierung, Nutzungsdauer in Monaten): Die Felder des Hauses stehen hier
 * noch als Text, siehe unten bei den Spalten.
 */
function alsZahl(text: string, spalte: Spalte): string | null {
  return liesFeldzahl(text, spalte.darstellung, spalte.art === 'zahl')
}

/** Keine Hausfelder — eine Konstante, damit die Spalten nicht bei jedem Zeichnen neu entstehen. */
const OHNE_HAUSFELDER: Record<string, { art: Feldart; vorrat: string[] | null }> = {}

/**
 * Als welche Spalte ein Hausfeld in der Tafel steht (R554; R839, R840).
 *
 * **Die Zahl eines Hausfelds ist eine Kommazahl.** Die Feldart `zahl` heißt
 * in der Tafel sonst „ganze Zahl des Werks" (Sortierung, Nutzungsdauer); der
 * Dienst liest die Zahl des Hauses aber mit Nachkommastellen, und das Blatt
 * nimmt sie so an. Ja/Nein steht als Auswahl aus *Ja* und *Nein* —
 * gespeichert bleibt `true` oder `false`. Unbekannt heißt: Text.
 */
function hausfeldart(art: Feldart | undefined): string {
  switch (art) {
    case 'zahl':
      return 'dezimal'
    case 'periode':
    case 'auswahl':
    case 'jaNein':
      return art
    default:
      return 'text'
  }
}

function imBereich(a: Zelle, b: Zelle) {
  return {
    vonZeile: Math.min(a.zeile, b.zeile),
    bisZeile: Math.max(a.zeile, b.zeile),
    vonSpalte: Math.min(a.spalte, b.spalte),
    bisSpalte: Math.max(a.spalte, b.spalte),
  }
}

export function Stammdatentafel({
  art,
  eintraege,
  hausbeschriftungen,
  hausfeldbeschreibungen = OHNE_HAUSFELDER,
  vorrat,
  laeuft,
  gewaehlt,
  aufWahl,
  aufSchreiben,
  codewort,
  nurLesen = false,
}: {
  art: Objektartbeschreibung
  eintraege: Listeneintrag[]
  hausbeschriftungen: Record<string, Record<string, string>>
  /** Feldart und Vorrat je Hausfeld — damit es steht wie ein Feld des Werks (R839, R840). */
  hausfeldbeschreibungen?: Record<string, { art: Feldart; vorrat: string[] | null }>
  /** Die Katalogeinträge je Verweisart — damit eine Kennung als Code dasteht. */
  vorrat: Record<string, Listeneintrag[]>
  laeuft: boolean
  gewaehlt: string | null
  aufWahl: (schluessel: string) => void
  /**
   * Schreibt eine Zeile. Die Tafel ruft je betroffener Zeile **einmal** —
   * gesammelt wird in ihr, nicht darunter.
   */
  aufSchreiben: (schluessel: string, auftrag: Pflegeauftrag) => Promise<void>
  /**
   * Wie der Code einer Zeile heißt — beim Verweis der Name des Ziels (R705).
   *
   * **Dieselbe Stelle wie in der Seitenliste, nicht eine zweite** (R838,
   * Befund vom 2026-09-29): Im Beteiligungsregister stand als *Eintrag* die
   * Kennung der Tochter, weil die Tafel den Code roh nahm, während die Liste
   * daneben ihn längst auflöste. Wer die Tafel ohne diese Angabe aufruft,
   * bekommt den Code, wie er ist.
   */
  codewort?: (code: string) => string
  /**
   * Ohne Recht zum Pflegen (8.1 Abschnitt 7, R1180): kein Eingabefeld auf
   * Doppelklick oder Eingabetaste, kein Einfügen — Auswählen und Kopieren
   * bleiben, denn sie schreiben nichts.
   */
  nurLesen?: boolean
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('stammdatentafel:stammdatentafel')

  const [anker, setAnker] = useState<Zelle | null>(null)
  const [bis, setBis] = useState<Zelle | null>(null)
  const [schreibt, setSchreibt] = useState<Zelle | null>(null)
  const [fortschritt, setFortschritt] = useState<string | null>(null)
  const rahmen = useRef<HTMLDivElement>(null)

  const spalten = useMemo<Spalte[]>(() => {
    const ausFeld = (
      feld: string,
      beschriftung: string,
      art2: string,
      v: string[],
      verweis: string | null,
      darstellung: Zahlendarstellung | null = null,
    ): Spalte =>
      ({ feld, beschriftung, art: art2, vorrat: v, verweisart: verweis, darstellung, nurLesen: false })

    const liste: Spalte[] = []

    // **Der Code steht vorn, auch wenn er ein Feld ist.** Bei der Beteiligung
    // ist `tochter` beides — Kennung der Zeile und Eigenschaft. Zweimal
    // stünde er albern da.
    //
    // **Und gar nicht, wo es keinen gibt** (R676): Dann ist der Code der
    // Schlüssel, und eine Spalte davon beantwortete keine Frage — die
    // Bezeichnung daneben trägt die Nummer schon (R678).
    const codefeld = art.codefeld
    const codeIstFeld = art.felder.some((f) => f.feld === codefeld)
    if (codefeld !== null && !codeIstFeld) {
      liste.push({
        feld: codefeld,
        beschriftung: t(`stammdaten.feld.${codefeld}` as Sprachschluessel),
        art: 'text',
        vorrat: [],
        verweisart: null,
        darstellung: null,
        nurLesen: true,
      })
    }

    // **Nicht jede Objektart zeigt alle ihre Felder** (R412): Wo jemand
    // gezählt und gestrichen hat, steht die Auswahl in TAFELSPALTEN --
    // und in der Reihenfolge, die dort steht, denn sie ist die Aussage.
    const gewaehlteFelder = TAFELSPALTEN[art.objektart]
    const felder = gewaehlteFelder
      ? gewaehlteFelder
          .map((name) => art.felder.find((f) => f.feld === name))
          .filter((f): f is (typeof art.felder)[number] => f !== undefined)
      : art.felder

    for (const f of felder) {
      liste.push(ausFeld(
        f.feld,
        feldname(art.objektart, f.feld),
        f.art,
        f.vorrat ?? [],
        f.verweisart,
        // Ob Anteil, Betrag oder Kurs, sagt die Feldbeschreibung (R840).
        f.darstellung ?? null,
      ))
    }

    // **Ein Feld des Hauses steht wie ein Feld des Werks** (R839, R840;
    // geschlossen am 2026-09-30). Bis dahin lieferte die Liste zu einem
    // Hausfeld nur seine Beschriftung, und die Tafel zeigte es als Text:
    // eine Periode als „202612", eine Zahl in der Speicherform, ein Ja/Nein
    // als „true". Das Blatt wusste die Art schon.
    for (const [feld, namen] of Object.entries(hausbeschriftungen)) {
      const b = hausfeldbeschreibungen[feld]
      liste.push(ausFeld(
        feld,
        namen[Object.keys(namen)[0] ?? 'de'] ?? feld,
        hausfeldart(b?.art),
        b?.art === 'jaNein' ? ['true', 'false'] : b?.vorrat ?? [],
        null,
      ))
    }

    return liste
  }, [art, hausbeschriftungen, hausfeldbeschreibungen])

  /** Was in der Zelle steht — **lesbar**, nicht als Schlüssel (R157). */
  function anzeige(eintrag: Listeneintrag, spalte: Spalte): string {
    const roh = spalte.feld === art.codefeld && spalte.nurLesen
      ? eintrag.code
      : eintrag.werte[spalte.feld] ?? ''

    if (!roh) {
      return ''
    }

    if (spalte.verweisart) {
      const ziel = (vorrat[spalte.verweisart] ?? []).find((k) => k.schluessel === roh)
      // **Dieselbe Ordnung wie im Zeilenkopf** (Befund vom 2026-09-26):
      // Code, sonst Bezeichnung, sonst Schlüssel. Bis dahin stand hier
      // `ziel.code` allein -- und Gesellschaft, Bewertungsebene und
      // Währung führen gar keinen Code. Die Zelle wäre leer gewesen,
      // wenn der Vorrat schon geladen war, und trug den Schlüssel, wenn
      // nicht. Beides sagt dem Anwender nichts.
      return ziel ? lesbar(ziel) : roh
    }

    // **Eine Periode steht als Monat und Jahr da** (R839 — Eine Periode liest
    // sich als Monat und Jahr). Bis zum 2026-09-30 gab die Zelle den
    // gespeicherten Wert heraus: Im Beteiligungsregister stand „Gültig ab
    // Periode 202603" und „Erwerbsperiode 202603".
    if (istPeriode(spalte)) {
      return periodenwort(roh)
    }

    // **Eine Zahl steht wie im Bericht** (R840 — Eine Zahl in den Stammdaten
    // steht wie im Bericht). Bis zum 2026-09-30 gab die Zelle die Speicherform
    // heraus: Im Beteiligungsregister stand der Kapitalanteil als
    // „0.80000000" und die Anschaffungskosten als „1600000.00". Jetzt steht
    // dort „80 %" und „1.600.000,00". Was die Kommazahl ist, sagt die
    // Darstellung; ohne sie steht die Zahl im Zahlenformat des Anwenders und
    // ohne die Endnullen der Spalte.
    if (istZahl(spalte)) {
      return zahlenwort(roh, spalte.darstellung)
    }

    return spalte.vorrat.length > 0 ? wertwort(spalte.feld, roh) : roh
  }

  /**
   * Was aus der Zelle in die Zwischenablage geht.
   *
   * **Überall das, was dasteht — nur Periode und Zahl gehen so, wie man sie
   * tippt.** „März 2026" macht die Tabellenkalkulation zu einem Datum und gibt
   * beim Zurückkopieren „Mrz 26" her; `202603` bleibt eine Zahl und kommt als
   * dieselbe zurück. Das Einfügen liest sie wieder — wie jede andere
   * Schreibweise des Periodenfelds.
   *
   * **Eine Zahl geht so hinaus, wie sie im Eingabefeld stünde** (R840): im
   * Zahlenformat des Anwenders, der Anteil als Prozentwert ohne Zeichen —
   * „80", nicht „80 %" und nicht der Bruch „0,8". Drei Gründe:
   *
   * 1. **Kopieren und Einfügen in dieselbe Tafel erhält den Wert.** Das
   *    Einfügen liest mit derselben Lesung wie das Feld, und was im Feld steht,
   *    liest das Feld wieder — sonst änderte schon das Öffnen und Verlassen
   *    einer Zelle ihren Wert.
   * 2. **Der Bruch käme falsch zurück.** Ein Anteil wird in Prozent gelesen
   *    (Entscheidung 2 vom 2026-09-30); aus „0,8" würden 0,8 %.
   * 3. **Das Prozentzeichen kostet in der Tabellenkalkulation Stellen.**
   *    „33,3333 %" wird dort eine Prozentzelle, die „33,33 %" zeigt und beim
   *    Zurückkopieren auch nur das hergibt. „33,3333" bleibt eine Zahl mit
   *    allen Stellen.
   */
  function ablagewert(eintrag: Listeneintrag, spalte: Spalte): string {
    if (istPeriode(spalte)) {
      return eintrag.werte[spalte.feld] ?? ''
    }

    if (istZahl(spalte)) {
      const roh = eintrag.werte[spalte.feld]
      return roh ? alsEingabe(roh, spalte.darstellung) : ''
    }

    return anzeige(eintrag, spalte)
  }

  /**
   * Der Rückweg: aus dem, was jemand einfügt, wird ein Wert des Modells.
   *
   * **Beschriftung und Schlüssel werden beide angenommen.** Wer aus der Tafel
   * kopiert, hat „Tochterunternehmen (§ 290 HGB)" in der Zwischenablage; wer
   * aus einer Vorlage kommt, vielleicht `tochter`. Beides zurückzuweisen wäre
   * eine Hürde ohne Gewinn — und stillschweigend das Falsche zu nehmen, wäre
   * schlimmer.
   */
  function alsWert(text: string, spalte: Spalte): string | null | undefined {
    const sauber = text.trim()
    if (!sauber) {
      return null
    }

    if (spalte.verweisart) {
      const ziel = (vorrat[spalte.verweisart] ?? []).find(
        (k) => k.code.toLowerCase() === sauber.toLowerCase() || k.schluessel === sauber,
      )
      return ziel ? ziel.schluessel : undefined
    }

    if (spalte.vorrat.length > 0) {
      const treffer = spalte.vorrat.find(
        (v) => v === sauber || wertwort(spalte.feld, v).toLowerCase() === sauber.toLowerCase(),
      )
      return treffer ?? undefined
    }

    // **Eine Periode wird gelesen wie im Periodenfeld** (R839, R502): `03.2026`,
    // `03/2026`, `2026-03` und `202603` sind derselbe März. Ohne diese Lesung
    // ginge „03.2026" als Text in den Bestand — der Fehler, den das
    // Anlageformular bis zum 2026-09-29 hatte. Geschrieben wird die
    // Speicherform; was sich nicht lesen lässt, wird gemeldet.
    if (istPeriode(spalte)) {
      const periode = ausPeriodenfeld(sauber)
      return periode === null ? undefined : String(periode)
    }

    // **Eine Zahl wird gelesen wie im Zahlenfeld** (R840): „1.600.000,00" und
    // „1600000" sind derselbe Betrag, „80" und „80 %" derselbe Anteil — und
    // geschrieben wird die Speicherform, beim Anteil der Bruch. Bis zum
    // 2026-09-30 ging der eingefügte Text ungelesen an den Dienst, und der
    // antwortete auf „80 %" mit einem technischen Fehler. Was sich nicht lesen
    // lässt, wird gemeldet und nicht geschrieben.
    if (istZahl(spalte)) {
      return alsZahl(sauber, spalte) ?? undefined
    }

    return sauber
  }

  const bereich = anker && bis ? imBereich(anker, bis) : null

  function istGewaehlt(zeile: number, spalte: number) {
    return bereich !== null
      && zeile >= bereich.vonZeile && zeile <= bereich.bisZeile
      && spalte >= bereich.vonSpalte && spalte <= bereich.bisSpalte
  }

  function waehle(zelle: Zelle, erweitern: boolean) {
    setSchreibt(null)
    if (erweitern && anker) {
      setBis(zelle)
    } else {
      setAnker(zelle)
      setBis(zelle)
    }
    rahmen.current?.focus()
  }

  /** Eine ganze Spalte auf einmal — der Griff, um den es im Auftrag geht. */
  function waehleSpalte(spalte: number) {
    if (eintraege.length === 0 || spalten[spalte]?.nurLesen) {
      return
    }

    setSchreibt(null)
    setAnker({ zeile: 0, spalte })
    setBis({ zeile: eintraege.length - 1, spalte })
    rahmen.current?.focus()
  }

  /**
   * Schreibt einen Block in die Auswahl.
   *
   * **Ein Wert füllt alles.** Sonst wird der Block ab dem Anker gelegt und
   * endet, wo er endet — kein Wiederholen und kein Abschneiden mit Meldung:
   * Was dasteht, ist genau das, was in der Zwischenablage stand.
   */
  async function fuege(block: string[][]) {
    if (!bereich || block.length === 0) {
      return
    }

    const einWert = block.length === 1 && block[0]!.length === 1
    const auftraege = new Map<string, Record<string, string | null>>()
    let unbekannt = 0
    // Was sich nicht als Periode liest — eigens gezählt, weil der Satz dazu
    // ein anderer ist: Eine Auswahlliste gibt es dort nicht.
    let unlesbar = 0
    // Und was sich nicht als Zahl liest (R840) — aus demselben Grund.
    let keineZahl = 0

    const zeilenBis = einWert
      ? bereich.bisZeile
      : Math.min(eintraege.length - 1, bereich.vonZeile + block.length - 1)

    for (let z = bereich.vonZeile; z <= zeilenBis; z++) {
      const quellzeile = einWert ? block[0]! : block[z - bereich.vonZeile]!
      const spaltenBis = einWert
        ? bereich.bisSpalte
        : Math.min(spalten.length - 1, bereich.vonSpalte + quellzeile.length - 1)

      for (let s = bereich.vonSpalte; s <= spaltenBis; s++) {
        const spalte = spalten[s]!
        if (spalte.nurLesen) {
          continue
        }

        const wert = alsWert(einWert ? block[0]![0]! : quellzeile[s - bereich.vonSpalte]!, spalte)
        if (wert === undefined) {
          if (istPeriode(spalte)) {
            unlesbar++
          } else if (istZahl(spalte)) {
            keineZahl++
          } else {
            unbekannt++
          }

          continue
        }

        const eintrag = eintraege[z]!
        const bisher = auftraege.get(eintrag.schluessel) ?? {}
        bisher[spalte.feld] = wert
        auftraege.set(eintrag.schluessel, bisher)
      }
    }

    // **Was sich nicht als Periode liest, wird ebenso benannt** (R839) — mit
    // den Schreibweisen, die das Feld annimmt.
    const periodensatz =
      unlesbar > 0
        ? `${t('tafel.einfuegen.keineperiode', { anzahl: anzahlwort(unlesbar) })} ${t('periode.muster.hinweis')}`
        : ''

    // **Und was sich nicht als Zahl liest** (R840, R145) — ein Satz, kein
    // technischer Fehler des Dienstes.
    const zahlensatz = keineZahl > 0 ? t('tafel.einfuegen.keinezahl', { anzahl: anzahlwort(keineZahl) }) : ''

    // **Was nicht im Vorrat steht, wird benannt und nicht geschluckt** (R145).
    if (unbekannt + unlesbar + keineZahl > 0 && auftraege.size === 0) {
      setFortschritt(
        [
          unbekannt > 0 ? t('tafel.einfuegen.unbekannt', { anzahl: anzahlwort(unbekannt) }) : '',
          periodensatz,
          zahlensatz,
        ]
          .filter(Boolean)
          .join(' '),
      )
      return
    }

    let fertig = 0
    for (const [schluessel, felder] of auftraege) {
      setFortschritt(t('tafel.einfuegen.laeuft', { fertig: anzahlwort(fertig), gesamt: anzahlwort(auftraege.size) }))
      await aufSchreiben(schluessel, { felder })
      fertig++
    }

    /*
      **Ganz geschrieben sagt das Kästchen** (7.2 Abschnitt 34, R1130, R1131)
      — ohne „12 Zeilen geschrieben". Was nicht geschrieben wurde, bleibt als
      Satz an der Tafel stehen: Das ist kein Erfolg, sondern ein Befund (R145).
    */
    if (unbekannt === 0) {
      meldeErfolg(t('erfolg.tafel.eingefuegt'))
    }
    setFortschritt(
      [
        unbekannt > 0
          ? t('tafel.einfuegen.teilweise', { fertig: anzahlwort(fertig), unbekannt: anzahlwort(unbekannt) })
          : '',
        periodensatz,
        zahlensatz,
      ]
        .filter(Boolean)
        .join(' ') || null,
    )
  }

  /** Aus der Auswahl wird ein Block, wie Excel ihn liest: Tabulator und Zeilenumbruch. */
  function alsBlock(): string {
    if (!bereich) {
      return ''
    }

    const zeilen: string[] = []
    for (let z = bereich.vonZeile; z <= bereich.bisZeile; z++) {
      const felder: string[] = []
      for (let s = bereich.vonSpalte; s <= bereich.bisSpalte; s++) {
        felder.push(ablagewert(eintraege[z]!, spalten[s]!))
      }

      zeilen.push(felder.join('\t'))
    }

    return zeilen.join('\n')
  }

  useEffect(() => {
    const kasten = rahmen.current
    if (!kasten) {
      return
    }

    function aufKopieren(e: ClipboardEvent) {
      if (!bereich || schreibt) {
        return
      }

      e.clipboardData?.setData('text/plain', alsBlock())
      e.preventDefault()
      // Ohne „3 Zeilen × 4 Spalten" (7.2 Abschnitt 34, R1131).
      setFortschritt(null)
      meldeErfolg(t('erfolg.tafel.kopiert'))
    }

    function aufEinfuegen(e: ClipboardEvent) {
      if (nurLesen || !bereich || schreibt || laeuft) {
        return
      }

      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (!text) {
        return
      }

      e.preventDefault()
      // Ein abschließender Zeilenumbruch ist beim Kopieren aus Excel die Regel
      // und keine leere Zeile.
      void fuege(text.replace(/\r\n?/g, '\n').replace(/\n$/, '').split('\n').map((z) => z.split('\t')))
    }

    kasten.addEventListener('copy', aufKopieren as EventListener)
    kasten.addEventListener('paste', aufEinfuegen as EventListener)
    return () => {
      kasten.removeEventListener('copy', aufKopieren as EventListener)
      kasten.removeEventListener('paste', aufEinfuegen as EventListener)
    }
  })

  function aufTaste(e: React.KeyboardEvent) {
    if (schreibt || !anker) {
      return
    }

    const grenze = (z: Zelle): Zelle => ({
      zeile: Math.max(0, Math.min(eintraege.length - 1, z.zeile)),
      spalte: Math.max(0, Math.min(spalten.length - 1, z.spalte)),
    })

    const schritte: Record<string, Zelle> = {
      ArrowUp: { zeile: -1, spalte: 0 },
      ArrowDown: { zeile: 1, spalte: 0 },
      ArrowLeft: { zeile: 0, spalte: -1 },
      ArrowRight: { zeile: 0, spalte: 1 },
    }

    const schritt = schritte[e.key]
    if (schritt) {
      e.preventDefault()
      const bezug = e.shiftKey ? (bis ?? anker) : anker
      const ziel = grenze({ zeile: bezug.zeile + schritt.zeile, spalte: bezug.spalte + schritt.spalte })
      if (e.shiftKey) {
        setBis(ziel)
      } else {
        setAnker(ziel)
        setBis(ziel)
      }

      return
    }

    if (e.key === 'Enter' && bis && anker.zeile === bis.zeile && anker.spalte === bis.spalte) {
      if (!nurLesen && !spalten[anker.spalte]!.nurLesen) {
        e.preventDefault()
        setSchreibt(anker)
      }

      return
    }

    if (e.key === 'Delete' && bereich) {
      e.preventDefault()
      void fuege([['']])
    }
  }

  if (eintraege.length === 0) {
    return null
  }

  return (
    <div className="stammdatentafel">
      {/*
        **Keine stehende Erklärung** (R608). Was hier steht, ist das Ergebnis
        des letzten Griffs — und solange keiner geschehen ist, steht nichts.
      */}
      {fortschritt && <p className="tafelstand">{fortschritt}</p>}

      <div
        className="tabellenrahmen"
        ref={rahmen}
        tabIndex={0}
        role="grid"
        aria-label={t(`stammdaten.art.${art.objektart}` as Sprachschluessel)}
        onKeyDown={aufTaste}
      >
        <table ref={spaltenrahmen} className="tafel ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th className="zeilenkopf" scope="col">{t('tafel.zeile')}</th>
              {spalten.map((s, i) => (
                <th
                  key={s.feld}
                  scope="col"
                  // Der Kopf einer Zahlenspalte steht rechts wie ihre Zellen (R763, R840).
                  className={(s.nurLesen ? 'nurlesen' : 'waehlbar') + (istZahl(s) ? ' zahl' : '')}
                  onClick={() => waehleSpalte(i)}
                  /*
                    Bei dreizehn Spalten nebeneinander ist jeder Kopf
                    abgeschnitten (R606). Der volle Name steht deshalb im
                    Titel — und dahinter, was ein Klick hier tut.
                  */
                  title={s.nurLesen ? s.beschriftung : `${s.beschriftung} — ${t('tafel.spaltewaehlen')}`}
                >
                  {s.beschriftung}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {eintraege.map((eintrag, z) => (
              <tr
                key={eintrag.schluessel}
                aria-current={eintrag.schluessel === gewaehlt ? true : undefined}
                className={eintrag.schluessel === gewaehlt ? 'gewaehlt' : undefined}
              >
                <th scope="row" className="zeilenkopf">
                  <button type="button" className="ortsverweis" onClick={() => aufWahl(eintrag.schluessel)}>
                    {codewort && eintrag.code ? codewort(eintrag.code) : lesbar(eintrag)}
                  </button>
                </th>
                {spalten.map((s, sp) => (
                  <td
                    key={s.feld}
                    className={
                      (istGewaehlt(z, sp) ? 'inauswahl ' : '')
                      + (s.nurLesen ? 'nurlesen' : '')
                      // **Eine Zahl steht rechts** (R763, R840) — wie in jedem Bericht.
                      + (istZahl(s) ? ' zahl' : '')
                    }
                    onMouseDown={(e) => waehle({ zeile: z, spalte: sp }, e.shiftKey)}
                    onDoubleClick={() => !nurLesen && !s.nurLesen && setSchreibt({ zeile: z, spalte: sp })}
                  >
                    {schreibt && schreibt.zeile === z && schreibt.spalte === sp ? (
                      <Zellenfeld
                        spalte={s}
                        wert={eintrag.werte[s.feld] ?? ''}
                        beschriftung={(v) => (s.vorrat.length > 0 ? wertwort(s.feld, v) : v)}
                        vorrat={s.verweisart ? vorrat[s.verweisart] ?? [] : []}
                        aufUnlesbar={() =>
                          setFortschritt(t(istZahl(s) ? 'tafel.zelle.keinezahl' : 'periode.ungueltig'))}
                        aufFertig={(neu) => {
                          setSchreibt(null)
                          rahmen.current?.focus()
                          if (neu !== null) {
                            void aufSchreiben(eintrag.schluessel, { felder: { [s.feld]: neu || null } })
                          }
                        }}
                      />
                    ) : (
                      anzeige(eintrag, s)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Was in der ersten Spalte steht — **nie nichts** (R143).
 *
 * **Der Befund vom 2026-09-26:** In der Tafel der Konsolidierungskreise
 * stand eine Zeile ohne ersten Eintrag. Der Kreis war angelegt, hieß
 * „Konzern Holding AG" und trug keinen Code — und der Zeilenkopf zeigte nur
 * den Code. Ergebnis: ein leerer Knopf, den man weder sieht noch trifft.
 * *„Es gibt den Ort, jedoch keinen auswählbaren und konfigurierbaren
 * Kreis."*
 *
 * **Die Spalte heißt *Eintrag* und nicht *Code*.** Sie soll die Zeile
 * benennen, und dafür ist der Code nur der kürzeste von drei Wegen: Wo er
 * fehlt, steht die Bezeichnung, und wo auch die fehlt, der Schlüssel. Der
 * Schlüssel sagt wenig — aber er trifft, und man kann ihn anklicken.
 *
 * **Eine Stelle für alle Objektarten**, nicht eine Ausnahme für Kreise:
 * Jede Objektart, deren Code freiwillig ist, hat dasselbe Problem.
 */
export function lesbar(eintrag: Listeneintrag): string {
  return eintrag.code || beschriftung(eintrag.namen) || eintrag.schluessel
}

/**
 * Das Bedienelement **in** der Zelle — und nur dort, wo gerade geschrieben
 * wird.
 *
 * Stünde es in jeder Zelle, wäre die Tabelle nicht mehr kopierbar: Eine
 * Klappliste gibt beim Kopieren nichts her, und aus 18 × 13 Zellen würde ein
 * Feld aus Leerstellen.
 */
function Zellenfeld({
  spalte,
  wert,
  vorrat,
  beschriftung,
  aufFertig,
  aufUnlesbar,
}: {
  spalte: Spalte
  wert: string
  vorrat: Listeneintrag[]
  beschriftung: (wert: string) => string
  aufFertig: (neu: string | null) => void
  /** Die Zelle wurde mit etwas verlassen, das sich nicht als Periode oder Zahl liest. */
  aufUnlesbar: () => void
}) {
  const [eingabe, setEingabe] = useState(wert)

  if (istPeriode(spalte)) {
    return <Periodenzelle spalte={spalte} wert={wert} aufFertig={aufFertig} aufUnlesbar={aufUnlesbar} />
  }

  if (istZahl(spalte)) {
    return <Zahlenzelle spalte={spalte} wert={wert} aufFertig={aufFertig} aufUnlesbar={aufUnlesbar} />
  }

  if (spalte.vorrat.length > 0 || spalte.verweisart) {
    const eintraege = spalte.verweisart
      ? vorrat.map((k) => ({ wert: k.schluessel, text: k.code, stillgelegtAb: stillgelegtAus(k.werte) }))
      : spalte.vorrat.map((v) => ({ wert: v, text: beschriftung(v) }))

    return (
      <select
        autoFocus
        aria-label={spalte.beschriftung}
        value={eingabe}
        onChange={(e) => {
          setEingabe(e.target.value)
          aufFertig(e.target.value)
        }}
        onBlur={() => aufFertig(null)}
        onKeyDown={(e) => e.key === 'Escape' && aufFertig(null)}
      >
        <option value="">—</option>
        {eintraege.map((e) => (
          <option key={e.wert} value={e.wert}>{e.text}</option>
        ))}
      </select>
    )
  }

  return (
    <input
      autoFocus
      aria-label={spalte.beschriftung}
      value={eingabe}
      onChange={(e) => setEingabe(e.target.value)}
      onBlur={() => aufFertig(eingabe === wert ? null : eingabe)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          aufFertig(eingabe === wert ? null : eingabe)
        } else if (e.key === 'Escape') {
          aufFertig(null)
        }
      }}
    />
  )
}

/**
 * Eine Periode in der Zelle — im selben Feld wie überall (R839 — Eine Periode
 * liest sich als Monat und Jahr; R502 — Am Bildschirm steht MM.JJJJ).
 *
 * **Bis zum 2026-09-30 stand hier das freie Textfeld mit `202603` darin**, und
 * was getippt wurde, ging ungelesen in den Bestand. Jetzt steht die Feldform
 * `03.2026` im Feld, jede Schreibweise des Periodenfelds wird angenommen, und
 * geschrieben wird die Speicherform.
 *
 * **Die Zelle schließt wie ihre Nachbarn:** Eingabetaste und Verlassen
 * schreiben, Esc verwirft. Das Periodenfeld kennt diese drei Griffe nicht —
 * es meldet bei jedem Tastendruck —, deshalb liest die Hülle darum, was im
 * Feld steht, wenn einer von ihnen geschieht.
 *
 * **Was sich nicht lesen lässt, wird nicht geschrieben.** Bei der
 * Eingabetaste bleibt das Feld offen; beim Verlassen schließt es, und der
 * Satz über der Tafel sagt, welche Schreibweisen gelten. Ein leeres Feld
 * nimmt die Angabe weg — wie in jeder anderen Zelle.
 */
function Periodenzelle({
  spalte,
  wert,
  aufFertig,
  aufUnlesbar,
}: {
  spalte: Spalte
  wert: string
  aufFertig: (neu: string | null) => void
  aufUnlesbar: () => void
}) {
  const feld = useRef<HTMLInputElement>(null)
  // **Geschlossen wird einmal.** Die Eingabetaste schließt die Zelle, und der
  // Rahmen nimmt den Fokus zurück — das Feld meldet daraufhin sein Verlassen,
  // und ohne diese Marke würde derselbe Wert zweimal geschrieben.
  const geschlossen = useRef(false)
  const bestand = alsPeriode(wert)

  useEffect(() => {
    feld.current?.focus()
    feld.current?.select()
  }, [])

  function schliesse(neu: string | null) {
    if (!geschlossen.current) {
      geschlossen.current = true
      aufFertig(neu)
    }
  }

  function lies(ziel: EventTarget, beimVerlassen: boolean) {
    if (!(ziel instanceof HTMLInputElement) || geschlossen.current) {
      return
    }

    if (ziel.value.trim() === '') {
      schliesse(bestand === null ? null : '')
      return
    }

    const gelesen = ausPeriodenfeld(ziel.value)
    if (gelesen === null) {
      if (beimVerlassen) {
        aufUnlesbar()
        schliesse(null)
      }

      return
    }

    schliesse(gelesen === bestand ? null : String(gelesen))
  }

  return (
    <span
      className="periodenwert"
      onBlur={(e) => lies(e.target, true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          lies(e.target, false)
        } else if (e.key === 'Escape') {
          schliesse(null)
        }
      }}
    >
      <Periodenfeld
        wert={bestand}
        // Geschrieben wird beim Schließen der Zelle, nicht bei jedem Tastendruck.
        aufWert={() => {}}
        beschriftung={spalte.beschriftung}
        klasse="nurfeld"
        erstesFeld={feld}
      />
    </span>
  )
}

/**
 * Eine Zahl in der Zelle — im selben Feld wie überall (R840 — Eine Zahl in den
 * Stammdaten steht wie im Bericht).
 *
 * **Bis zum 2026-09-30 stand hier das freie Textfeld mit `0.80000000` darin**,
 * und was getippt wurde, ging ungelesen an den Dienst: „80 %" beim
 * Kapitalanteil und „1.600.000,00" bei den Anschaffungskosten endeten in einem
 * technischen Fehler. Jetzt steht die Zahl in der Hausschreibweise im Feld —
 * der Anteil als „80" mit dem Prozentzeichen daneben —, und geschrieben wird
 * die Speicherform, beim Anteil der Bruch.
 *
 * **Die Zelle schließt wie die Periode daneben:** Eingabetaste und Verlassen
 * schreiben, Esc verwirft. Gelesen wird, was im Feld steht, wenn einer der
 * drei Griffe geschieht — nicht bei jedem Tastendruck, denn „1" ist auf dem
 * Weg zu „1.600.000" schon eine Zahl.
 *
 * **Was sich nicht lesen lässt, wird nicht geschrieben.** Bei der Eingabetaste
 * bleibt das Feld offen; beim Verlassen schließt es, und der Satz über der
 * Tafel sagt es. Ein leeres Feld nimmt die Angabe weg. **Und dieselbe Zahl in
 * anderer Schreibweise ist keine Änderung:** „80" über `0.80000000` schreibt
 * nichts — sonst entstünde mit jedem Blick in die Zelle eine Fassung (R241).
 */
function Zahlenzelle({
  spalte,
  wert,
  aufFertig,
  aufUnlesbar,
}: {
  spalte: Spalte
  wert: string
  aufFertig: (neu: string | null) => void
  aufUnlesbar: () => void
}) {
  const feld = useRef<HTMLInputElement>(null)
  // Geschlossen wird einmal — siehe die Periode darüber.
  const geschlossen = useRef(false)

  useEffect(() => {
    feld.current?.focus()
    feld.current?.select()
  }, [])

  function schliesse(neu: string | null) {
    if (!geschlossen.current) {
      geschlossen.current = true
      aufFertig(neu)
    }
  }

  function lies(ziel: EventTarget, beimVerlassen: boolean) {
    if (!(ziel instanceof HTMLInputElement) || geschlossen.current) {
      return
    }

    if (ziel.value.trim() === '') {
      schliesse(wert === '' ? null : '')
      return
    }

    const gelesen = alsZahl(ziel.value, spalte)
    if (gelesen === null) {
      if (beimVerlassen) {
        aufUnlesbar()
        schliesse(null)
      }

      return
    }

    // Verglichen wird über die Speicherform ohne Endnullen: `0.8` gegen
    // `0.80000000` ist keine Änderung.
    schliesse(speicherform(gelesen) === speicherform(wert) ? null : gelesen)
  }

  return (
    <span
      className="zahlenwert"
      onBlur={(e) => lies(e.target, true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          lies(e.target, false)
        } else if (e.key === 'Escape') {
          schliesse(null)
        }
      }}
    >
      <Zahlenfeld
        wert={wert || null}
        // Geschrieben wird beim Schließen der Zelle, nicht bei jedem Tastendruck.
        aufWert={() => {}}
        darstellung={spalte.darstellung}
        beschriftung={spalte.beschriftung}
        klasse="nurfeld"
        erstesFeld={feld}
      />
    </span>
  )
}
